import { eventCountdown, formatEventWhen, EVENT_TYPE_COLORS } from '../hooks/useEvents';

// Composes the feed's hero carousel from whatever real content exists right
// now. Replaces the hardcoded `featuredItems` mock, whose three cards pointed
// at mock video ids (silently dead once the mock videos go) and, for the Event
// card, at nothing at all.
//
// The invariant that matters: a slot with no destination is DROPPED. A dead
// hero card can't be produced by this function, so the carousel shrinks to
// however much real content exists instead of padding itself with filler.
//
// Sources are additive — Events plugged in here without the carousel itself
// changing, and a `featured_items` table could later replace any one source
// without touching the render.


const eventSlot = (event) => {
  // Registration link if there is one, otherwise the calendar. Never nothing.
  const dest = event.registrationUrl
    ? { href: event.registrationUrl }
    : { to: '/explore/events' };

  const where = event.isVirtual ? (event.location || 'Virtual') : event.location;

  return {
    key: `event-${event.id}`,
    kind: 'event',
    // The countdown is the badge now, not buried mid-subtitle. "In 4 days" is
    // the reason this slot exists; "Event" was a category label saying nothing
    // the card didn't already show.
    badge: eventCountdown(event.startsAt),
    title: event.title,
    // Split into two lines so the card can give WHEN its own weight. The
    // concrete date and time (with zone) is what someone needs to decide
    // whether they can attend; host and place are secondary.
    when: formatEventWhen(event.startsAt, event.endsAt),
    subtitle: [event.host, where].filter(Boolean).join(' · '),
    image: event.imageUrl || null,
    accent: EVENT_TYPE_COLORS[event.type] || 'var(--brand-primary)',
    cta: event.registrationUrl ? 'Register' : 'View details',
    ...dest,
  };
};

const videoSlot = (video) => ({
  key: `video-${video.id}`,
  kind: 'video',
  badge: video.isCurated ? 'Staff Pick' : 'Featured',
  title: video.title,
  subtitle: [video.creator, video.duration].filter(Boolean).join(' · '),
  image: video.thumbnail || null,
  accent: 'var(--brand-primary)',
  cta: 'Watch',
  to: `/video/${video.id}`,
});

// "Founding Sponsor", not "Sponsored" — it matches the landing page's own
// language and accurately describes the relationship. It is still a disclosure:
// it names the commercial tie rather than hiding it.
//
// This slot renders in a LIGHT variant (see HeroCard). A sponsor logo cannot
// use the photographic treatment the other slots use: these are logos on white
// or transparent grounds at wildly different aspect ratios (864x193 vs 640x466),
// so a full-bleed crop under a dark scrim would mangle them.
// A malformed sponsor URL drops the slot rather than rendering a card whose
// only action goes nowhere — the invariant this module is built on.
const isUsableUrl = (value) => {
  try {
    return !!new URL(value);
  } catch {
    return false;
  }
};

const sponsorSlot = (sponsor) => {
  if (!isUsableUrl(sponsor.url)) return null;

  return {
    key: `sponsor-${sponsor.id}`,
    kind: 'sponsor',
    variant: 'light',
    badge: 'Founding Sponsor',
    title: sponsor.name,
    subtitle: sponsor.description || null,
    logo: sponsor.logo,
    image: null,
    accent: 'var(--brand-primary)',
    // No description or footer text is rendered on the slide any more — the
    // mark carries it. The URL parse above stays regardless: it is the guard
    // that DROPS a sponsor with a malformed URL rather than rendering a dead
    // card, which is the invariant this whole module is built on.
    cta: 'Visit website',
    href: sponsor.url,
  };
};

export function buildHeroSlots({
  videos = [],
  events = [],
  sponsor = null,
  max = 4,
  maxEvents = 2,
} = {}) {
  const slots = [];

  // Events lead: they're the only time-bound content here, so they're the only
  // content with a reason to be seen today rather than whenever. Featured
  // first — otherwise the admin's Featured checkbox controls nothing, which is
  // the dead-control problem this refactor exists to remove.
  const orderedEvents = [
    ...events.filter((e) => e.featured),
    ...events.filter((e) => !e.featured),
  ];
  orderedEvents.slice(0, maxEvents).forEach((e) => slots.push(eventSlot(e)));

  if (sponsor) {
    const slot = sponsorSlot(sponsor);
    if (slot) slots.push(slot);
  }

  // Fill remaining space with real curated video, preferring Staff Picks and
  // requiring a thumbnail — a hero card with no art has nothing to show.
  const heroVideos = videos.filter((v) => v.thumbnail);
  const curatedFirst = [
    ...heroVideos.filter((v) => v.isCurated),
    ...heroVideos.filter((v) => !v.isCurated),
  ];
  for (const v of curatedFirst) {
    if (slots.length >= max) break;
    slots.push(videoSlot(v));
  }

  return slots
    .filter((s) => s.to || s.href)   // no destination, no card
    .slice(0, max);
}
